/**
 * SettingsShell — layout for the /app/settings/* + /admin/settings/* trees.
 *
 * Renders INSIDE AppShell's <main> outlet, so the app's icon rail and top bar
 * stay visible above/beside it (per user requirement: main sidebar always
 * visible alongside settings sidebar). Composition:
 *
 *     [icon rail]  [top bar                             ]
 *                  [settings sidebar | active settings page]
 *
 * On mobile: settings sidebar is hidden. The main hamburger in the TopBar
 * opens a drawer that renders both the app icon rail AND the settings
 * sidebar side-by-side when the current route is a settings route. See
 * components/layout/mobile-nav-drawer.tsx for that behavior.
 */

import { Outlet } from "react-router-dom";
import { SettingsSidebar } from "@/pages/settings/settings-sidebar";
import { useAuth } from "@/hooks/use-auth";

type Props = {
  /** URL prefix — "/app/settings" or "/admin/settings". Passed to child links. */
  basePath: "/app/settings" | "/admin/settings";
};

export function SettingsShell({ basePath }: Props) {
  const { role, workspaceName } = useAuth();

  // Bounded-height, two-column, each-column-owns-its-scroll pattern.
  //
  // Why not sticky: main (in AppShell) has `overflow-hidden` which makes it a
  // scroll ancestor, so `sticky top-16` inside would stick 64px BELOW main's
  // top edge (which is already below the TopBar) — visible as a ~130px empty
  // gap above the sidebar. Instead we size the whole shell to exactly the
  // viewport minus the TopBar and let each column overflow-y-auto internally.
  //
  // TopBar height differs by breakpoint: h-14 on mobile (56px), h-16 on
  // desktop (64px). Match both. The sidebar itself is hidden below md —
  // mobile uses the main hamburger drawer for settings nav.
  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[calc(100dvh-4rem)]">
      <div className="hidden md:contents">
        <SettingsSidebar basePath={basePath} role={role} />
      </div>
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-4xl px-4 py-6 md:px-8 md:py-8">
          <Outlet context={{ basePath, workspaceName, role }} />
        </div>
      </div>
    </div>
  );
}
